import type { TrackingFn } from '@change-corgi/core/react/tracking';
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { trackShareCancel, trackShareFailure, trackShareSuccess } from './track';

/**
 * Configuration options for Facebook sharing functionality.
 *
 * @remarks
 * The tracking events follow a specific order:
 * 1. `intent` - Fires when the share process starts, regardless of method (SDK or URL)
 * 2. `legacy_success` - Fires after `intent` to maintain compatibility with existing dashboards/metrics
 * 3. `success` - Behavior varies by method:
 *    - SDK: Fires when share completes successfully
 *    - URL: Fires when share dialog opens
 * 4. `failure` or `cancel` - Fires based on the share outcome
 *
 * @property trackingData - Key-value pairs for tracking metadata
 * @property trackingEvents - Event names for different stages of the share flow
 * @property trackingEvents.intent - Event fired when share process begins
 * @property trackingEvents.legacy_success - Event fired after intent (for dashboard compatibility)
 * @property trackingEvents.success - Event fired on successful share (timing varies by method)
 * @property trackingEvents.failure - Event fired when share fails
 * @property trackingEvents.cancel - Event fired when share is cancelled
 * @property mode - Optional configuration for share method by platform (defaults to SDK if not specified)
 * @property mode.mobile - Share method to use on mobile devices ('sdk' or 'url')
 * @property mode.desktop - Share method to use on desktop devices ('sdk' or 'url')
 */
export type Options = {
	trackingData: Record<string, string | number | boolean | undefined>;
	trackingEvents: {
		intent: string;
		legacy_success: string;
		success: string;
		failure: string;
		cancel: string;
	};
	mode?: { mobile: 'sdk' | 'url'; desktop: 'sdk' | 'url' };
};

type ShareWithParams = {
	url: string;
	options: Options;
	utilityContext: UtilityContext;
	track: TrackingFn;
	isDesktop: boolean;
};

const DESKTOP_SHARE_URL = 'https://www.facebook.com/dialog/share';
const MOBILE_SHARE_URL = 'https://m.facebook.com/dialog/feed';

export async function shareWithUrl({
	url,
	options,
	utilityContext,
	track,
	isDesktop,
}: ShareWithParams): Promise<boolean> {
	const { trackingEvents, trackingData } = options;
	const { errorReporter, facebook } = utilityContext;
	try {
		const shareUrl = isDesktop ? new URL(DESKTOP_SHARE_URL) : new URL(MOBILE_SHARE_URL);
		shareUrl.searchParams.append('app_id', facebook.appId);
		shareUrl.searchParams.append('href', url);

		if (!isDesktop) {
			shareUrl.searchParams.append('link', url);
		}

		await Promise.resolve(window.open(shareUrl.toString(), '_blank'));

		trackShareSuccess(trackingEvents.success, trackingData, utilityContext, track);

		return true;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		void errorReporter.report(new Error(`Could not share to facebook: ${e}`));

		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
		trackShareFailure(trackingEvents.failure, { error: e.toString() }, trackingData, utilityContext, track);
		return false;
	}
}

export async function shareWithSdk({
	url,
	options,
	utilityContext,
	track,
	isDesktop,
}: ShareWithParams): Promise<boolean> {
	const { trackingEvents, trackingData } = options;
	const { errorReporter, facebook } = utilityContext;

	try {
		let params: fb.ShareOpenGraphDialogParams | fb.ShareDialogParams;

		if (isDesktop) {
			params = {
				// the SDK defaults to iframe on desktop when the user
				// is logged in with their FB account
				display: 'popup',
				action_type: 'og.likes',
				action_properties: {
					object: url,
				},
				href: url,
				method: 'share_open_graph',
			};
		} else {
			/**
			 * On mobile, the `share_open_graph` method does not work properly,
			 * so we should keep using the `share` method until we can confirm
			 * migrating is safe.
			 */
			params = {
				href: url,
				method: 'share',
			};
		}

		const response = await facebook.ui(params);

		if (response.cancelled) {
			trackShareCancel(trackingEvents.cancel, trackingData, utilityContext, track);
			return false;
		}

		trackShareSuccess(trackingEvents.success, trackingData, utilityContext, track);

		return true;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		void errorReporter.report(new Error(`Could not share to facebook: ${e}`));

		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
		trackShareFailure(trackingEvents.failure, { error: e.toString() }, trackingData, utilityContext, track);
		return false;
	}
}
